import { Button, Flex, Group, Skeleton, Text, Textarea } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { type JSX, useCallback } from "react";

import type { SummaryResolution } from "@/shared-types";

import { withPolymorphicClickAnalytics } from "@/analytics";
import { RadioCard } from "@/common-ui";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useAgentSdk, useAgentStore, usePartner } from "@/sdk";

import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { CallReasonInput } from "./CallReasonInput";
import { DispositionInput } from "./DispositionInput";
import classes from "./EditSessionSummaryModal.module.css";
import { SendSummaryNotification } from "./SendSummaryNotification";
import { SessionSummarySubject } from "./SessionSubject";
import { getNotificationText, MAX_SUMMARY_LENGTH } from "./utils";

interface SessionSummaryFormProps {
    endSession: (reason: "AgentTerminatedWrapping" | "WrappingTimeExpired") => Promise<void>;
    loading: boolean;
    validAccountNumber: boolean;
    setView: (view: "edit-summary" | "schedule-callback") => void;
    callbackRemaining: number;
    simplifiedSummary: boolean;
    sendingSummary: boolean;
}

const resolutionStatusLabels: { resolutionStatus: SummaryResolution; label: string }[] = [
    { resolutionStatus: "resolved", label: "Yes" },
    { resolutionStatus: "not resolved", label: "No" },
    { resolutionStatus: "transferred", label: "Transferred" },
];
const CallbackCustomerButton = withPolymorphicClickAnalytics(Button<"button">, "Click");
const SendSummaryButton = withPolymorphicClickAnalytics(Button<"button">, "Click");

export function SessionSummaryForm({
    endSession,
    validAccountNumber = false,
    loading,
    setView,
    callbackRemaining,
    simplifiedSummary,
    sendingSummary,
}: SessionSummaryFormProps): JSX.Element | null {
    const { height } = useViewportSize();
    const { pendingActivity } = useAgentStore();
    const partner = usePartner();
    const { callbackEnabled: isCallbackEnabled } = usePartnerFeatureConfigured();
    const { offlineAgentActivity } = useAgentSdk();
    const { sessionSummary: sessionSummaryConfig } = usePartnerConfig();
    const { callReasons: customCallReasons, dispositions: customDispositions } = sessionSummaryConfig ?? {};

    const {
        setSessionSummary,
        setResolved,
        setCategory,
        setSubcategory,
        hideResolutionStatusForm,
        summary,
        resolutionStatus,
        autogeneratedSummary,
        submitted,
    } = useSessionSummaryStore();

    const handleResolution = useCallback(
        (resolution: SummaryResolution) => {
            setResolved(resolution);
        },
        [setResolved],
    );

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setSessionSummary({ value: event.target.value });
        },
        [setSessionSummary],
    );

    async function handleEndSessionClick() {
        await endSession("AgentTerminatedWrapping");
    }

    function handleScheduleCallbackClick() {
        setView("schedule-callback");
    }
    const {
        requiresManualCopy,
        features: { isResolutionStatusFormEnabled = true },
    } = usePartnerConfig();
    const shouldShowResolutionForm =
        isResolutionStatusFormEnabled &&
        !(hideResolutionStatusForm && resolutionStatus !== null) &&
        !simplifiedSummary &&
        !customDispositions;

    const endSessionLabel =
        (validAccountNumber && !requiresManualCopy) || simplifiedSummary ? "Send session summary" : "Copy summary";

    const getEndSessionLabel = () => {
        if (simplifiedSummary) {
            return validAccountNumber && !submitted
                ? "Send session summary"
                : validAccountNumber && submitted
                  ? "Summary sent"
                  : "Copy summary";
        } else {
            return pendingActivity === offlineAgentActivity ? "End session and log off" : "End session";
        }
    };

    return (
        <>
            {customCallReasons && (
                <CallReasonInput
                    callReasons={customCallReasons}
                    onChange={(callReason) => {
                        setCategory(callReason.reason);
                        setSubcategory(callReason.subReason);
                    }}
                />
            )}
            {shouldShowResolutionForm && (
                <>
                    <Text size="sm" mb=".5rem">
                        Was the customer’s issue resolved?
                    </Text>
                    <Group mb="1.6rem">
                        {resolutionStatusLabels.map((resolution) => (
                            <RadioCard
                                id={`${resolution.resolutionStatus}-radio-button`}
                                analytics={{
                                    actionId: `SessionSummaryModal_ProblemSolved${resolution.label}`,
                                }}
                                key={resolution.resolutionStatus}
                                color="var(--mantine-color-dark-5)"
                                label={resolution.label}
                                onClick={() => handleResolution(resolution.resolutionStatus)}
                                p="0.5rem 0.75rem"
                                selected={resolutionStatus === resolution.resolutionStatus}
                                data-testid={`session-summary-problem-solved-${resolution.label.toLowerCase()}`}
                                autoFocus={false}
                                tabIndex={-1}
                            />
                        ))}
                    </Group>
                </>
            )}
            {customDispositions && <DispositionInput dispositions={customDispositions} onChange={handleResolution} />}
            {simplifiedSummary && <SessionSummarySubject loading={loading} />}
            <Text size="sm" mb=".5rem">
                Notes
            </Text>
            {loading ? (
                <Flex direction="column" align="center" h="256px">
                    <Skeleton mb="xxs" h="100%" />
                </Flex>
            ) : (
                <Flex className={classes.modalTextareaContainer} direction="column">
                    <>
                        <Textarea
                            className="fs-mask"
                            classNames={{
                                input: classes.modalTextarea,
                            }}
                            maxLength={MAX_SUMMARY_LENGTH}
                            mr="0.125rem"
                            mt="3xs"
                            mb="0.3rem"
                            style={{
                                scrollbarColor: "var(--mantine-color-gray-7) var(--mantine-color-dark-7)",
                            }}
                            onChange={handleChange}
                            fz="xs"
                            placeholder="Enter session summary here..."
                            disabled={(simplifiedSummary && !!autogeneratedSummary && !!summary) || submitted}
                            rows={height >= 650 ? 7 : 4}
                            value={summary}
                            data-testid="session-summary-text-input"
                        />
                        <SendSummaryNotification
                            validAccountNumber={validAccountNumber}
                            summary={summary}
                            notificationText={getNotificationText(partner, validAccountNumber)}
                        />
                    </>
                </Flex>
            )}
            <Flex id="modal-actions" justify="flex-end" pt="md" gap="xs">
                {isCallbackEnabled && callbackRemaining > 0 ? (
                    <CallbackCustomerButton
                        analytics={{
                            actionId: "SessionSummaryModal_CallCustomerButton",
                        }}
                        color="primary.3"
                        size="xs"
                        fz="xs"
                        onClick={handleScheduleCallbackClick}
                        variant="outline"
                    >
                        Schedule callback
                    </CallbackCustomerButton>
                ) : null}
                <SendSummaryButton
                    analytics={{
                        actionId: "SessionSummaryModal_SendButton",
                    }}
                    className={classes.sendSummaryButton}
                    aria-label={endSessionLabel}
                    disabled={
                        loading ||
                        !summary ||
                        (resolutionStatus === null && !simplifiedSummary && isResolutionStatusFormEnabled) ||
                        submitted
                    }
                    size="xs"
                    loading={sendingSummary}
                    fz="xs"
                    onClick={() => void handleEndSessionClick()}
                    data-testid="call-controls-end-session"
                >
                    {getEndSessionLabel()}
                </SendSummaryButton>
            </Flex>
        </>
    );
}
